import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { Seo } from '@/components/seo/Seo';
import { z } from 'zod';
import { Header } from '@/components/Header';
import { trackEvent } from '@/lib/analytics';
import {
  normalizeTemplateContinuationPath,
  pendingAppTemplateContinuation,
  rememberAppTemplateContinuation,
} from '@/lib/app-templates';

const emailSchema = z.string().email("Please enter a valid email address");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters long");

const Auth = () => {
  // Get invite token from URL immediately
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('invite');
  const urlEmail = params.get('email');
  const requestedRedirectPath = params.get('redirect');
  const redirectPath = requestedRedirectPath || pendingAppTemplateContinuation();
  
  const [email, setEmail] = useState(urlEmail || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [inviteToken, setInviteToken] = useState(urlToken || '');
  const [inviteError, setInviteError] = useState<string>('');
  const [inviteChecking, setInviteChecking] = useState<boolean>(!!urlToken);
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    isAnonymous,
    anonymousTransferPending,
    anonymousTransferError,
    retryAnonymousTransfer,
  } = useAuth();

  useEffect(() => {
    if (requestedRedirectPath) rememberAppTemplateContinuation(requestedRedirectPath);
  }, [requestedRedirectPath]);

  // Validate invite token on mount (new invites table or legacy waitlist)
  useEffect(() => {
    if (!urlToken) {
      setInviteChecking(false);
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('verify-invite', {
          body: { token: urlToken, email: urlEmail || undefined },
        });
        if (error) {
          setInviteError('Could not verify this invite link.');
        } else if (!data?.valid) {
          const reason = data?.reason;
          const msg =
            reason === 'expired' ? 'This invite link has expired.' :
            reason === 'already_used' ? 'This invite link has already been used.' :
            reason === 'revoked' ? 'This invite link has been revoked.' :
            'This invite link is invalid or no longer active.';
          setInviteError(msg);
        } else if (data?.email && (!urlEmail || data.email.toLowerCase() !== (urlEmail || '').toLowerCase())) {
          // Lock email to invite's email (override URL param)
          setEmail(data.email);
        }
      } catch (e) {
        setInviteError('Could not verify this invite link.');
      } finally {
        setInviteChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const validateEmail = (email: string) => {
    try {
      emailSchema.parse(email);
      setEmailError('');
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setEmailError(error.errors[0].message);
      }
      return false;
    }
  };

  const validatePassword = (password: string) => {
    try {
      passwordSchema.parse(password);
      setPasswordError('');
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setPasswordError(error.errors[0].message);
      }
      return false;
    }
  };

  // Redirect if already authenticated (skip anonymous guests so they can sign in/up)
  useEffect(() => {
    if (!authLoading && user && !isAnonymous && !anonymousTransferPending) {
      if (redirectPath && redirectPath.startsWith('/')) {
        navigate(redirectPath);
      } else {
        navigate('/');
      }
    }
  }, [user, isAnonymous, authLoading, anonymousTransferPending, navigate, redirectPath]);

  const retryGuestWorkTransfer = async () => {
    setLoading(true);
    await retryAnonymousTransfer();
    setLoading(false);
  };

  const signUp = async () => {
    // Check if invite-only mode (check if there's a waitlist entry for this email)
    if (!inviteToken) {
      trackEvent('signup_started', { status: 'invite_required' });
      toast({
        title: "Invite Required",
        description: "Registration is by invitation only. Please apply for beta access.",
        variant: "destructive",
      });
      const templateContinuation = normalizeTemplateContinuationPath(redirectPath);
      navigate(templateContinuation
        ? `/beta_access?redirect=${encodeURIComponent(templateContinuation)}`
        : '/beta_access');
      return;
    }

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);
    trackEvent('signup_started', { status: 'submitted' });
    try {
      // Normalize email to lowercase for case-insensitive comparison
      const normalizedEmail = email.trim().toLowerCase();
      
      // Use edge function to signup with invite and auto-confirm
      const { data, error } = await supabase.functions.invoke('signup-with-invite', {
        body: { email: normalizedEmail, password, inviteToken },
      });

      if (error || !data?.success) {
        toast({
          title: "Error",
          description: error?.message || data?.error || "Failed to create account",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Set the session from the response
      if (data.session) {
        await supabase.auth.setSession(data.session);
        trackEvent('signup_completed', { status: 'success' });
        
        toast({
          title: "Welcome!",
          description: "Your account has been created successfully.",
        });
        
        navigate(redirectPath && redirectPath.startsWith('/') ? redirectPath : data.isAuthor ? '/for_expert' : '/');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const signIn = async () => {
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);
    try {
      // Normalize email to lowercase for case-insensitive comparison
      const normalizedEmail = email.trim().toLowerCase();
      
      const { error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        let errorMessage = error.message;
        if (error.message === "Invalid login credentials") {
          errorMessage = "Invalid email or password. Please check your credentials or sign up if you don't have an account.";
        }
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const resetPassword = async () => {
    const isEmailValid = validateEmail(email);
    if (!isEmailValid) {
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();

      const { data, error } = await supabase.functions.invoke('send-reset-email', {
        body: {
          email: normalizedEmail,
          redirectUrl: window.location.origin,
        },
      });

      if (error || !data?.success) {
        toast({
          title: "Error",
          description: error?.message || data?.error || "Failed to send reset email",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Password reset email sent. Please check your inbox.",
        });
        setShowForgotPassword(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  return (
    <>
      <Seo title="Applyt: Sign in" description="Sign in to Applyt: your digital twin for decision-making." />
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted">
        <Header hideUnauthenticatedActions />
        <div className="flex items-center justify-center p-4 pt-20">
          <div className="w-full max-w-md space-y-8">
            {/* Auth Form */}
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-center">Welcome</CardTitle>
              </CardHeader>
          <CardContent className="space-y-4">
            {anonymousTransferError && (
              <div role="alert" className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <p>{anonymousTransferError}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void retryGuestWorkTransfer()}
                  disabled={loading || authLoading}
                >
                  Restore my work
                </Button>
              </div>
            )}
            <Tabs defaultValue={inviteToken ? "signup" : "signin"} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
                <TabsTrigger
                  value="expert"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/become_expert');
                  }}
                >
                  I am an expert
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin" className="space-y-4">
                {!showForgotPassword ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        placeholder="Enter your email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) validateEmail(e.target.value);
                        }}
                        disabled={loading}
                        className={emailError ? "border-destructive" : ""}
                      />
                      {emailError && (
                        <p className="text-sm text-destructive">{emailError}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        placeholder="Enter your password"
                        type="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) validatePassword(e.target.value);
                        }}
                        disabled={loading}
                        className={passwordError ? "border-destructive" : ""}
                      />
                      {passwordError && (
                        <p className="text-sm text-destructive">{passwordError}</p>
                      )}
                    </div>
                    <Button 
                      onClick={signIn} 
                      disabled={loading} 
                      className="w-full"
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                    <div className="text-center">
                      <Button
                        variant="link"
                        onClick={() => setShowForgotPassword(true)}
                        disabled={loading}
                        className="text-sm text-muted-foreground hover:text-primary"
                      >
                        Forgot your password?
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input
                        id="reset-email"
                        placeholder="Enter your email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) validateEmail(e.target.value);
                        }}
                        disabled={loading}
                        className={emailError ? "border-destructive" : ""}
                      />
                      {emailError && (
                        <p className="text-sm text-destructive">{emailError}</p>
                      )}
                    </div>
                    <Button 
                      onClick={resetPassword} 
                      disabled={loading} 
                      className="w-full"
                    >
                      {loading ? "Sending..." : "Send Reset Email"}
                    </Button>
                    <div className="text-center">
                      <Button
                        variant="link"
                        onClick={() => setShowForgotPassword(false)}
                        disabled={loading}
                        className="text-sm text-muted-foreground hover:text-primary"
                      >
                        Back to sign in
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>
              
              <TabsContent value="signup" className="space-y-4">
                {!inviteToken && (
                  <div className="bg-muted/50 p-4 rounded-lg text-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Registration is by invitation only
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/beta_access')}
                      className="w-full"
                    >
                      Apply for Beta Access
                    </Button>
                  </div>
                )}
                {inviteToken && inviteChecking && (
                  <div className="bg-muted/50 p-4 rounded-lg text-center text-sm text-muted-foreground">
                    Verifying your invite link...
                  </div>
                )}
                {inviteToken && !inviteChecking && inviteError && (
                  <div className="bg-destructive/10 border border-destructive/30 p-4 rounded-lg text-center space-y-2">
                    <p className="text-sm text-destructive font-medium">{inviteError}</p>
                    <p className="text-xs text-muted-foreground">
                      Please contact the person who sent you the link, or request a new invite.
                    </p>
                  </div>
                )}
                {inviteToken && !inviteChecking && !inviteError && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="invite-code">Invitation Code</Label>
                      <Input
                        id="invite-code"
                        placeholder="Your invitation code"
                        type="text"
                        value={inviteToken}
                        disabled
                        className="bg-muted"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        placeholder="Enter your email"
                        type="email"
                        value={email}
                        disabled
                        className="bg-muted"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <Input
                        id="signup-password"
                        placeholder="Create a password (min. 6 characters)"
                        type="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) validatePassword(e.target.value);
                        }}
                        disabled={loading}
                        className={passwordError ? "border-destructive" : ""}
                      />
                      {passwordError && (
                        <p className="text-sm text-destructive">{passwordError}</p>
                      )}
                    </div>
                    <Button 
                      onClick={signUp} 
                      disabled={loading} 
                      className="w-full"
                    >
                      {loading ? "Creating account..." : "Complete Registration"}
                    </Button>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
    </>
  );
};

export default Auth;
